import type { MouseEvent } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { PencilSimpleIcon } from "@phosphor-icons/react";
import { startCase } from "lodash";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { z } from "zod";

import {
  Button,
  Input,
  Select,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { patchDetails } from "~/v2/api/rest/account";
import { SectionDropdown } from "../../../../components/common/SectionDropdown";
import { MARITAL_STATUS_VALUES } from "../../optionsOptIn/constant";
import { MARITAL_STATUS_OPTIONS } from "../../optionsOptIn/pages/financialProfile/MartialStatusInputs";
import type { IRAOnboardingProps } from "..";
import { SavedInput } from "./SavedInput";

const schema = z.object({
  marital_status: z.enum(MARITAL_STATUS_VALUES),
  number_of_dependents: z.coerce
    .number({ error: "Please input number" })
    .int({ message: "Must be integer" })
    .nonnegative({ message: "Cannot be negative" })
    .max(999),
});

type PersonalProfileSchema = z.infer<typeof schema>;

export interface PersonalProfileProps {
  tradeAccount: IRAOnboardingProps["tradeAccount"];
  tradeAccountDetails: IRAOnboardingProps["tradeAccountDetails"];
  refetchTradeAccountDetails: () => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const PersonalProfile = ({
  tradeAccount,
  tradeAccountDetails,
  refetchTradeAccountDetails,
  isEditing,
  setIsEditing,
  isOpen,
  setIsOpen,
}: PersonalProfileProps) => {
  const {
    watch,
    handleSubmit,
    setValue,
    register,
    formState: { errors },
  } = useForm<PersonalProfileSchema>({
    resolver: zodResolver(schema),
    values: {
      marital_status:
        tradeAccountDetails.marital_status as (typeof MARITAL_STATUS_VALUES)[number],
      number_of_dependents: tradeAccountDetails.number_of_dependents || 0,
    },
    mode: "onChange",
  });

  const maritalStatus = watch("marital_status");

  const saveDetailsMutation = useMutationRequest(
    (data: PersonalProfileSchema) =>
      patchDetails(tradeAccount.id, {
        ...data,
      }),
    {
      onSuccess: () => {
        refetchTradeAccountDetails();
        setIsEditing(false);
        toastSuccess("Personal profile updated");
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Failed to update personal profile: ${errorMessage}`);
      },
    },
  );

  const onToggleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!isOpen) {
      setIsOpen(true);
      return;
    }
    // only close when not editing
    if (isEditing) {
      return;
    }

    setIsOpen(false);
  };

  const onEditClicked = (event: MouseEvent<HTMLButtonElement>) => {
    // When button re-renders as Save button after this click it triggers form submission even with type=button on this Edit button
    event.preventDefault();
    setIsEditing(true);
    setIsOpen(true);
  };

  const onSubmitValid: SubmitHandler<PersonalProfileSchema> = (data) => {
    if (isEditing) {
      saveDetailsMutation.mutate(data);
    }
  };

  const onSubmitInvalid: SubmitErrorHandler<PersonalProfileSchema> = () => {
    toastError("Please fill in all fields");
  };

  return (
    <form onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}>
      <SectionDropdown
        title="Personal Profile"
        isOpen={isOpen}
        onToggleOpen={onToggleOpen}
        className="rounded-lg border border-gray-200 bg-white p-4"
        icon={
          !isEditing ? (
            <Button
              variant="v2/ghost"
              size="v2/small"
              type="button"
              onClick={onEditClicked}
            >
              <PencilSimpleIcon size={20} className="fill-gray-700" />
            </Button>
          ) : (
            <Button
              variant="v2/ghost"
              size="v2/small"
              type="submit"
              disabled={saveDetailsMutation.isPending}
            >
              Save
            </Button>
          )
        }
      >
        {isEditing ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2 sm:gap-3">
              <Select
                label="Marital Status"
                options={MARITAL_STATUS_OPTIONS}
                value={maritalStatus}
                onChange={(value) => setValue("marital_status", value)}
                errorMsg={errors.marital_status?.message}
                status={errors.marital_status ? "error" : "neutral"}
              />
              <Input
                label="Number of Dependents"
                errorMsg={errors.number_of_dependents?.message}
                status={errors.number_of_dependents ? "error" : "neutral"}
                type="number"
                {...register("number_of_dependents")}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            <SavedInput
              label="Marital Status"
              value={startCase(
                tradeAccountDetails.marital_status?.toLowerCase() || "",
              )}
            />
            <SavedInput
              label="Number of Dependents"
              value={tradeAccountDetails.number_of_dependents ?? ""}
            />
          </div>
        )}
      </SectionDropdown>
    </form>
  );
};
